import * as THREE from 'three';

type TextOpts = {
  kind?: 'number' | 'name';
  color?: string;
};

/** High-res canvas print for jersey name / number. */
export function createKitTextTexture(
  text: string,
  opts: TextOpts = {},
): THREE.CanvasTexture {
  const isName = opts.kind === 'name';
  const width = isName ? 1024 : 1024;
  const height = isName ? 280 : 1024;
  const fontSize = isName ? 160 : 560;
  // Dark print reads on light Porto / stripe kits
  const color = opts.color ?? '#1a1a1a';

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, width, height);

  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `900 ${fontSize}px "Bebas Neue", Impact, "Arial Black", sans-serif`;
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 3;
  const label = (text || '').toUpperCase();
  ctx.fillText(label, width / 2, height / 2 + (isName ? 6 : 10));

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Drop Sketchfab props that aren't the jersey mesh. */
export function stripNonShirtNodes(root: THREE.Object3D) {
  const kill: THREE.Object3D[] = [];
  root.traverse((o) => {
    const n = (o.name || '').toLowerCase();
    if (
      n.includes('backdrop') ||
      n.includes('baking') ||
      n.includes('camera') ||
      n.includes('physcamera') ||
      n.includes('indirect')
    ) {
      kill.push(o);
    }
  });
  kill.forEach((o) => o.parent?.remove(o));
}

/** Center + scale so the tallest axis ≈ targetSize (meters). */
export function fitObjectToSize(root: THREE.Object3D, targetSize = 1.05) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  root.position.x -= center.x;
  root.position.y -= center.y;
  root.position.z -= center.z;
  const maxDim = Math.max(size.x, size.y, size.z, 0.0001);
  const s = targetSize / maxDim;
  root.scale.setScalar(s);
  root.updateMatrixWorld(true);
}
